import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminAPI } from "../../utils/api";
import {
  Settings,
  Save,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import toast from "react-hot-toast";

const InputField = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  step,
  min,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <input
      type={type}
      step={step}
      min={min}
      className="input-field w-full"
      placeholder={placeholder}
      value={value || ""}
      onChange={onChange}
    />
  </div>
);

const AdminSettings = () => {
  const [settings, setSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const queryClient = useQueryClient();

  const {
    data: settingsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["adminSettings"],
    queryFn: () => adminAPI.settings(),
    retry: 2,
  });

  // Initialize local state when data loads
  useEffect(() => {
    if (settingsData?.data?.data) {
      setSettings(settingsData.data.data);
    }
  }, [settingsData]);

  const updateMutation = useMutation({
    mutationFn: (data) => adminAPI.updateSettings(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["adminSettings"] });
      toast.success(response.data?.message || "Settings updated successfully");
      setHasChanges(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update settings");
    },
  });

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!hasChanges) {
      toast.info("No changes to save");
      return;
    }
    updateMutation.mutate(settings);
  };

  const handleReset = () => {
    if (settingsData?.data?.data) {
      setSettings(settingsData.data.data);
      setHasChanges(false);
      toast.info("Changes discarded");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
        <p className="font-medium">Failed to load settings</p>
        <button
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ["adminSettings"] })
          }
          className="mt-3 btn-secondary text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Configure system-wide settings</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleReset}
            disabled={!hasChanges || updateMutation.isPending}
            className="btn-secondary flex items-center disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            className="btn-primary flex items-center disabled:opacity-50"
          >
            {updateMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {hasChanges && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          You have unsaved changes. Don't forget to save!
        </div>
      )}

      <div className="card">
        <div className="flex items-center mb-6">
          <div className="p-2 bg-primary-100 rounded-lg mr-3">
            <Settings className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              General Settings
            </h3>
            <p className="text-sm text-gray-500">
              Basic company and system configuration
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="Company Name"
              value={settings.company_name}
              onChange={(e) => handleChange("company_name", e.target.value)}
              placeholder="Courier Manager"
            />
            <InputField
              label="Contact Email"
              type="email"
              value={settings.contact_email}
              onChange={(e) => handleChange("contact_email", e.target.value)}
              placeholder="support@courier.com"
            />
          </div>

          <div className="border-t border-gray-100 pt-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-4">
              Pricing Configuration
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Base Delivery Fee ($)"
                type="number"
                step="0.01"
                min="0"
                value={settings.base_delivery_fee}
                onChange={(e) =>
                  handleChange("base_delivery_fee", e.target.value)
                }
                placeholder="5.00"
              />
              <InputField
                label="Weight Rate ($/kg)"
                type="number"
                step="0.01"
                min="0"
                value={settings.weight_rate}
                onChange={(e) => handleChange("weight_rate", e.target.value)}
                placeholder="2.00"
              />
            </div>
          </div>

          <div className="border-t border-gray-100 pt-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-4">
              Commission & Tax
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Tax Rate (%)"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={settings.tax_rate}
                onChange={(e) => handleChange("tax_rate", e.target.value)}
                placeholder="10"
              />
              <InputField
                label="Courier Commission (%)"
                type="number"
                step="1"
                min="0"
                max="100"
                value={settings.courier_commission}
                onChange={(e) =>
                  handleChange("courier_commission", e.target.value)
                }
                placeholder="70"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Read-only Info Card */}
      <div className="card bg-gray-50 border-gray-200">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">
          System Information
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Last Updated</p>
            <p className="font-medium text-gray-900">
              {settings.updated_at
                ? new Date(settings.updated_at).toLocaleDateString()
                : "N/A"}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Version</p>
            <p className="font-medium text-gray-900">
              {settings.version || "1.0.0"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
